import { Suspense, lazy } from 'react';
import { Navigate } from 'react-router-dom';
import { RouteObject } from 'react-router';

import SidebarLayout from 'src/layouts/SidebarLayout';

import SuspenseLoader from 'src/components/SuspenseLoader';
import BaseLayout from './layouts/BaseLayout';

const Loader = (Component) => (props) =>
  (
    <Suspense fallback={<SuspenseLoader />}>
      <Component {...props} />
    </Suspense>
  );

// Auth
const Login = Loader(lazy(() => import('src/views/auth/Login')));
const Profile = Loader(lazy(() => import('src/views/profile/profile')));

const Dashboard = Loader(lazy(() => import('src/views/dashboard/dashboard')));

const MemberList = Loader(
  lazy(() => import('src/views/memberManagement/memberList/memberList'))
);

const PointList = Loader(
  lazy(() => import('src/views/memberManagement/memberPoin/pointList'))
);
const DetailPoint = Loader(
  lazy(() => import('src/views/memberManagement/memberPoin/detailPoint'))
);

const ComplaintCenter = Loader(
  lazy(() => import('src/views/memberManagement/complaint/complaintCenter'))
);

const MemberVoucher = Loader(
  lazy(() => import('src/views/memberManagement/memberVoucher/memberVoucher'))
);
const MemberVoucherDetail = Loader(
  lazy(
    () => import('src/views/memberManagement/memberVoucher/detailMemberVoucher')
  )
);

const RegionList = Loader(
  lazy(() => import('src/views/partnerManagement/region/regionList'))
);
const AreaList = Loader(
  lazy(() => import('src/views/partnerManagement/areaList/areaList'))
);
const OutletList = Loader(
  lazy(() => import('src/views/partnerManagement/outletList/outletList'))
);
const AddOutlet = Loader(
  lazy(() => import('src/views/partnerManagement/outletList/addOutlet'))
);
const UpdatePageOutlet = Loader(
  lazy(() => import('src/views/partnerManagement/outletList/updatePageOutlet'))
);

const CategoryMaster = Loader(
  lazy(
    () =>
      import('src/views/catalogManagement/categoryMaster/categoryMasterList')
  )
);
const ProductList = Loader(
  lazy(() => import('src/views/catalogManagement/productList/productList'))
);
const AddProduct = Loader(
  lazy(() => import('src/views/catalogManagement/productList/addProduct'))
);
const UpdateProduct = Loader(
  lazy(() => import('src/views/catalogManagement/productList/updateProduct'))
);

const StoreFrontProductList = Loader(
  lazy(
    () =>
      import(
        'src/views/catalogManagement/storefrontProductList/storefrontProductList'
      )
  )
);
const StoreFrontProductSetting = Loader(
  lazy(
    () =>
      import(
        'src/views/catalogManagement/storefrontProductList/settingStorefrontProduct'
      )
  )
);

const PaymentHistory = Loader(
  lazy(() => import('src/views/transaction/paymentHistory/paymentHistory'))
);
const PaymentDetail = Loader(
  lazy(() => import('src/views/transaction/paymentHistory/paymentDetail'))
);
const OrderList = Loader(
  lazy(() => import('src/views/transaction/orderList/orderList'))
);
const OrderDetail = Loader(
  lazy(() => import('src/views/transaction/orderList/detailOrder'))
);

const VoucherHistory = Loader(
  lazy(
    () => import('src/views/transaction/voucherExchangeHistory/voucherHistory')
  )
);

const NewsPromotionList = Loader(
  lazy(
    () =>
      import('src/views/contentManagement/newsAndPromotions/newsPromotionList')
  )
);
const NewNewsPromotion = Loader(
  lazy(
    () =>
      import('src/views/contentManagement/newsAndPromotions/newNewsPromotion')
  )
);
const EditNewsPromotion = Loader(
  lazy(
    () =>
      import('src/views/contentManagement/newsAndPromotions/editNewsPromotion')
  )
);

const BadgeLevel = Loader(
  lazy(() => import('src/views/configuration/badgeLevel/badgeLevel'))
);

const VoucherManagement = Loader(
  lazy(
    () => import('src/views/configuration/voucherManagement/voucherManagement')
  )
);
const NewVoucherManagement = Loader(
  lazy(() => import('src/views/configuration/voucherManagement/newVoucher'))
);
const EditVoucherManagement = Loader(
  lazy(() => import('src/views/configuration/voucherManagement/editVoucher'))
);

const PointPayment = Loader(
  lazy(() => import('src/views/configuration/poinPayment/pointPayment'))
);

const Delivery = Loader(
  lazy(() => import('src/views/configuration/delivery/delivery'))
);

const ComplaintCategory = Loader(
  lazy(
    () => import('src/views/configuration/complaintCategory/complaintCategory')
  )
);

const AdminManagementList = Loader(
  lazy(
    () => import('src/views/configuration/adminManagement/adminManagementList')
  )
);
const NewAdminManagement = Loader(
  lazy(() => import('src/views/configuration/adminManagement/newAdmin'))
);
const UpdateAdminManagement = Loader(
  lazy(() => import('src/views/configuration/adminManagement/updateAdmin'))
);

const PrivacyPolicyMember = Loader(
  lazy(() => import('src/views/privacyPolicy/PrivacyPolicyMember'))
);
const PrivacyPolicyOutlet = Loader(
  lazy(() => import('src/views/privacyPolicy/PrivacyPolicyOutlet'))
);

const TermConditionMember = Loader(
  lazy(() => import('src/views/termCondition/TermConditionMember'))
);

// In the components
const Status404 = Loader(lazy(() => import('src/views/status/Status404')));
const Status500 = Loader(lazy(() => import('src/views/status/Status500')));
const StatusComingSoon = Loader(
  lazy(() => import('src/views/status/ComingSoon'))
);
const StatusMaintenance = Loader(
  lazy(() => import('src/views/status/Maintenance'))
);

const routes: RouteObject[] = [
  {
    path: 'auth',
    element: <BaseLayout />,
    children: [
      {
        path: 'login',
        element: <Login />
      },
      {
        path: 'forgot-password',
        element: <Login />
      }
    ]
  },
  {
    path: '/',
    element: <BaseLayout />,
    children: [
      {
        path: '',
        element: <Navigate to="/auth/login" replace />
      },
      {
        path: 'privacy-policy-member',
        element: <PrivacyPolicyMember />
      },
      {
        path: 'privacy-policy-outlet',
        element: <PrivacyPolicyOutlet />
      },
      {
        path: 'term-condition-member',
        element: <TermConditionMember />
      }
    ]
  },
  {
    path: '/',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <Navigate to="dashboard" replace />
      },
      {
        path: 'profile',
        element: <Profile />
      },
      {
        path: 'dashboard',
        element: <Dashboard />
      },
      {
        path: 'member-list',
        element: <MemberList />
      },

      {
        path: 'point',
        element: <PointList />
      },
      {
        path: 'point/detail/:id',
        element: <DetailPoint />
      },

      {
        path: 'complaint-center',
        element: <ComplaintCenter />
      },

      {
        path: 'member-vouchers',
        element: <MemberVoucher />
      },
      {
        path: 'member-vouchers/detail/:id',
        element: <MemberVoucherDetail />
      },

      {
        path: 'region',
        element: <RegionList />
      },
      {
        path: 'area-list',
        element: <AreaList />
      },
      {
        path: 'outlet-list',
        element: <OutletList />
      },
      {
        path: 'outlet-list/new',
        element: <AddOutlet />
      },
      {
        path: 'outlet-list/edit/:id',
        element: <UpdatePageOutlet />
      },
      {
        path: 'category-master',
        element: <CategoryMaster />
      },
      {
        path: 'product-list',
        element: <ProductList />
      },
      {
        path: 'product-list/new',
        element: <AddProduct />
      },
      {
        path: 'product-list/edit/:id',
        element: <UpdateProduct />
      },
      {
        path: 'storefront-product-list',
        element: <StoreFrontProductList />
      },
      {
        path: 'storefront-product-list/product-setting',
        element: <StoreFrontProductSetting />
      },

      {
        path: 'payment-history',
        element: <PaymentHistory />
      },
      {
        path: 'payment-history/detail/:id',
        element: <PaymentDetail />
      },
      {
        path: 'order-list',
        element: <OrderList />
      },
      {
        path: 'order-list/detail/:id',
        element: <OrderDetail />
      },

      {
        path: 'voucher-exchange-history',
        element: <VoucherHistory />
      },

      {
        path: 'news-promotion',
        element: <NewsPromotionList />
      },
      {
        path: 'news-promotion/new',
        element: <NewNewsPromotion />
      },
      {
        path: 'news-promotion/edit/:id',
        element: <EditNewsPromotion />
      },

      {
        path: 'badge-level-rule-configuration',
        element: <BadgeLevel />
      },

      {
        path: 'voucher-management-configuration',
        element: <VoucherManagement />
      },
      {
        path: 'voucher-management-configuration/new',
        element: <NewVoucherManagement />
      },
      {
        path: 'voucher-management-configuration/edit/:id',
        element: <EditVoucherManagement />
      },

      {
        path: 'payment-point-configuration',
        element: <PointPayment />
      },

      {
        path: 'delivery',
        element: <Delivery />
      },

      {
        path: 'complaint-category-configuration',
        element: <ComplaintCategory />
      },

      {
        path: 'admin-management-configuration',
        element: <AdminManagementList />
      },
      {
        path: 'admin-management-configuration/new',
        element: <NewAdminManagement />
      },
      {
        path: 'admin-management-configuration/edit/:id',
        element: <UpdateAdminManagement />
      },

      {
        path: '*',
        element: <Status404 />
      },
      {
        path: 'status',
        children: [
          {
            path: '',
            element: <Navigate to="404" replace />
          },
          {
            path: '404',
            element: <Status404 />
          },
          {
            path: '500',
            element: <Status500 />
          },
          {
            path: 'maintenance',
            element: <StatusMaintenance />
          },
          {
            path: 'coming-soon',
            element: <StatusComingSoon />
          }
        ]
      }
    ]
  }
];

export default routes;
